import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from './nav'
import { ExternalLink, ChevronRight, Code2, BookOpen, KeyRound, Github } from 'lucide-react'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import type { Project, Bug as BugType, BugBreakdownRow } from '../data/projectTypes'
import { isResolved } from '../../netlify/functions/lib/bug-statuses'
import InitialTestRunView from './InitialTestRunView'
import { OverviewStatCards, UnresolvedBugsHeader, OverviewTestRuns } from './OverviewSummary'
import { useTabAlert } from '../hooks/useTabAlert'
import { useInitialTestRun } from '../hooks/useInitialTestRun'
import { PROJECT_LIST_PAGE_SIZE, useGetBugBreakdownQuery, useGetBugCountsQuery, useGetProcessingSummaryQuery, useGetProjectBugsQuery, useGetProjectExplorationsQuery, useGetProjectJourneysInfiniteQuery, useGetProjectQuery, useGetProjectTestSessionsInfiniteQuery, useHasProjectUserSessionsQuery } from '../services/projectDataApi'
import './OverviewTab.css'

interface OverviewTabProps {
  projectId: string
}

// Interim view for the first test session — shared with ProjectOverview via useInitialTestRun.

// Single-hue red ramp — kept in sync with the --color-severity-* tokens (see index.css) so the pie
// slices match the severity pills exactly. Resolved is the lightest step of the ramp.
const SEVERITY_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#f97316',
  medium: '#eab308',
  low: '#3b82f6',
}

const RESOLVED_COLOR = '#e7d3d3'

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low'] as const

const CATEGORY_LABELS: Record<string, string> = {
  'testing': 'Testing',
  'network-performance': 'Network Performance',
  'react-rendering': 'React Rendering',
  'layout-shift': 'Layout Shift',
  'accessibility': 'Accessibility',
  'glitches': 'Glitches',
  'user-experience': 'User Experience',
  'seo': 'SEO',
}

export function BugBreakdownChart({ breakdown: breakdownProp, resolvedCount: resolvedCountProp, loading: loadingProp }: {
  breakdown?: BugBreakdownRow[]
  resolvedCount?: number
  loading?: boolean
} = {}) {
  const breakdownLoading = loadingProp ?? false
  const breakdown = breakdownProp ?? []
  const resolvedCount = resolvedCountProp ?? 0

  // Sum each severity across all categories into a single distribution for the donut. The category
  // split lives in the per-category breakdown; here we just want the overall severity mix (matching
  // the mockup's severity donut). A "Resolved" slice is appended when there are resolved bugs.
  const data = useMemo<{ key: string; name: string; value: number; color: string }[]>(() => {
    const totals: Record<'critical' | 'high' | 'medium' | 'low', number> = { critical: 0, high: 0, medium: 0, low: 0 }
    for (const row of breakdown) {
      totals.critical += row.critical
      totals.high += row.high
      totals.medium += row.medium
      totals.low += row.low
    }
    const slices = SEVERITY_ORDER
      .map(s => ({ key: s as string, name: s.charAt(0).toUpperCase() + s.slice(1), value: totals[s], color: SEVERITY_COLORS[s] ?? '#999' }))
      .filter(d => d.value > 0)
    if (resolvedCount > 0) {
      slices.push({ key: 'resolved', name: 'Resolved', value: resolvedCount, color: RESOLVED_COLOR })
    }
    return slices
  }, [breakdown, resolvedCount])

  const total = data.reduce((sum, d) => sum + d.value, 0)
  if (total === 0 && !breakdownLoading) return null

  return (
    <div className="overview-tab__chart-card" data-testid="bug-breakdown-chart">
      <h3 className="overview-tab__chart-title">Bug Breakdown</h3>
      {/* Solid pie with the legend below the chart. Keeping the legend under (not beside) the pie
          means the chart never collapses to nothing when its column gets narrow — the pie stays a
          centered square and the legend wraps into rows beneath it. */}
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="46%"
            innerRadius={0}
            outerRadius="78%"
            paddingAngle={1}
            stroke="var(--color-surface)"
            strokeWidth={2}
          >
            {data.map(d => (
              <Cell key={d.key} fill={d.color} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 12 }}
          />
          <Legend
            layout="horizontal"
            align="center"
            verticalAlign="bottom"
            iconType="circle"
            iconSize={9}
            wrapperStyle={{ fontSize: 12, lineHeight: '18px', paddingTop: 8 }}
            formatter={(value, entry) => (
              <span style={{ color: 'var(--color-text-secondary)' }}>
                {value} <span style={{ color: 'var(--color-text-muted)' }}>({(entry?.payload as { value?: number } | undefined)?.value ?? 0})</span>
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  )
}

const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

const TOP_OPEN_BUGS_LIMIT = 5

export function TopOpenBugs({ bugs, projectId, expandable = false }: { bugs: BugType[]; projectId: string; expandable?: boolean }) {
  // When expandable, the list starts collapsed to the top bugs and a "Show more" toggle extends it
  // in place to the remaining open bugs — no separate control or duplicate list. The classic
  // overview leaves expandable off and simply shows the top bugs.
  const [showAll, setShowAll] = useState(false)

  const openBugs = useMemo(() => {
    return bugs
      .filter(b => !isResolved(b.status))
      .sort((a, b) => (SEVERITY_RANK[a.severity] ?? 99) - (SEVERITY_RANK[b.severity] ?? 99))
  }, [bugs])

  if (openBugs.length === 0) return null

  const visible = expandable && showAll ? openBugs : openBugs.slice(0, TOP_OPEN_BUGS_LIMIT)
  const remaining = openBugs.length - TOP_OPEN_BUGS_LIMIT

  return (
    <div className="overview-tab__chart-card" data-testid="top-open-bugs">
      <h3 className="overview-tab__chart-title">Top Open Bugs</h3>
      <div className="overview-tab__bug-list">
        {visible.map(b => (
          <Link
            key={b.id}
            to={`/projects/${projectId}/bugs/${b.id}`}
            className="overview-tab__bug-row"
            data-analytics-event="project-overview-bug-link"
          >
            <span className={`overview-tab__bug-severity overview-tab__bug-severity--${b.severity}`}>
              {b.severity}
            </span>
            <span className="overview-tab__bug-title">{b.title}</span>
            <span className="overview-tab__bug-kind">
              {b.polish_category
                ? (CATEGORY_LABELS[b.polish_category] ?? b.polish_category)
                : 'testing'}
            </span>
            <ExternalLink size={12} className="overview-tab__bug-arrow" aria-hidden />
          </Link>
        ))}
      </div>
      {expandable && remaining > 0 && (
        <button
          type="button"
          className="overview-tab__bug-show-more"
          onClick={() => setShowAll(s => !s)}
          aria-expanded={showAll}
          data-testid="top-open-bugs-show-more"
          data-analytics-event="project-overview-top-bugs-show-more"
        >
          {showAll
            ? 'Show fewer'
            : `Show ${remaining} more open ${remaining === 1 ? 'bug' : 'bugs'}`}
          <ChevronRight size={14} className={`overview-tab__bug-show-more-icon${showAll ? ' overview-tab__bug-show-more-icon--open' : ''}`} aria-hidden />
        </button>
      )}
    </div>
  )
}

// A GitHub-synced project that won't run an initial exploration (no 'detect' deployment URL) has no
// activity to show on a fresh overview. Instead of a wall of zeroed-out cards, explain when QA will
// actually run, based on how main-branch / PR testing is configured.
function githubQaStatusMessage(github: NonNullable<Project['github']>): { title: string; text: string } {
  const { mainBranchTesting, prTesting, mainBranchDeploymentUrl } = github
  if (mainBranchTesting === 'detect' && !mainBranchDeploymentUrl?.trim()) {
    return {
      title: 'Add a deployment URL',
      text: 'Main-branch testing is set to detect deployments, but no deployment URL is configured. Add one in Settings → GitHub and QA will explore your app.',
    }
  }
  if (mainBranchTesting === 'events' || prTesting === 'events') {
    return {
      title: 'Waiting for a deployment',
      text: 'QA runs automatically when GitHub reports a new deployment. No initial exploration is run for event-driven projects — results will appear here after your first deployment.',
    }
  }
  if (mainBranchTesting === 'environment' || prTesting === 'environment') {
    return {
      title: 'Dedicated test environment',
      text: 'This project is set to spin up a dedicated test environment, which is coming soon. QA will run here once it’s available.',
    }
  }
  return {
    title: 'QA is off',
    text: 'Main-branch and pull-request testing are both set to None. Enable a testing mode in Settings → GitHub to start running QA.',
  }
}

function GithubQaStatus({ github }: { github: NonNullable<Project['github']> }) {
  const { title, text } = githubQaStatusMessage(github)
  return (
    <div className="overview-tab__proxy overview-tab__proxy--pending" role="status" data-testid="github-qa-status">
      <div className="overview-tab__proxy-header">
        <Github size={16} className="overview-tab__proxy-icon" aria-hidden />
        <span className="overview-tab__proxy-title">{title}</span>
      </div>
      <p className="overview-tab__proxy-text">{text}</p>
    </div>
  )
}

// Surface the REST API as a first-class workflow from the project's landing page. The Overview is
// where users (and their agents) start, but the API + OpenAPI spec + token were previously only
// discoverable from the Settings modal — so an agent landing here had no signal it could drive
// everything programmatically. This callout points at the endpoint, the machine-readable spec, and
// where to mint a token.
function ApiAccessCallout() {
  const siteUrl = window.location.origin
  return (
    <div className="overview-tab__api" data-testid="overview-api-callout">
      <div className="overview-tab__proxy-header">
        <Code2 size={16} className="overview-tab__api-icon" aria-hidden />
        <span className="overview-tab__proxy-title">Drive QA from the REST API</span>
      </div>
      <p className="overview-tab__proxy-text">
        Everything on this page is available programmatically at <code>{siteUrl}/api/v1</code> — create
        projects, list bugs, start explorations, and inspect results from scripts or AI assistants.
        Authenticate with an API token (<code>Authorization: Bearer lqa_…</code>); the machine-readable
        OpenAPI spec documents the full workflow.
      </p>
      <div className="overview-tab__api-actions">
        <a
          href={`${siteUrl}/api/v1/openapi.json`}
          className="overview-tab__api-link"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="overview-api-openapi-link"
          data-analytics-event="project-overview-api-openapi-link"
        >
          <BookOpen size={14} aria-hidden />
          <span>OpenAPI spec</span>
          <ExternalLink size={13} aria-hidden />
        </a>
        <Link
          to="?settings=api"
          className="overview-tab__api-link"
          data-testid="overview-api-token-link"
          data-analytics-event="project-overview-api-token-link"
        >
          <KeyRound size={14} aria-hidden />
          <span>Generate API token</span>
          <ChevronRight size={14} aria-hidden />
        </Link>
      </div>
    </div>
  )
}

export default memo(function OverviewTab({ projectId }: OverviewTabProps) {
  const explorationsQuery = useGetProjectExplorationsQuery({ projectId, page: 1, pageSize: 100 }, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const journeysQuery = useGetProjectJourneysInfiniteQuery({ projectId, parentId: null, pageSize: PROJECT_LIST_PAGE_SIZE }, { pollingInterval: 60_000, skipPollingIfUnfocused: true, refetchCachedPages: false })
  const testSessionsQuery = useGetProjectTestSessionsInfiniteQuery({ projectId, pageSize: PROJECT_LIST_PAGE_SIZE }, { pollingInterval: 60_000, skipPollingIfUnfocused: true, refetchCachedPages: false })
  const projectQuery = useGetProjectQuery(projectId)
  const userSessionsQuery = useHasProjectUserSessionsQuery(projectId, {
    pollingInterval: 60_000,
    skipPollingIfUnfocused: true,
  })
  const bugCountsQuery = useGetBugCountsQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const processingQuery = useGetProcessingSummaryQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const overviewBugsQuery = useGetProjectBugsQuery({ projectId, page: 1, pageSize: 20, hideResolved: true, severitySort: 'desc' }, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const bugBreakdownQuery = useGetBugBreakdownQuery(projectId, { pollingInterval: 60_000, skipPollingIfUnfocused: true })
  const explorations = {
    items: explorationsQuery.data?.items ?? [],
    loading: explorationsQuery.isLoading,
    currentProjectId: projectId,
    total: explorationsQuery.data?.total ?? 0,
  }
  const journeyPages = journeysQuery.data?.pages ?? []
  const journeys = {
    items: journeyPages.flatMap(page => page.items),
    loading: journeysQuery.isLoading,
    currentProjectId: projectId,
    totalAll: journeyPages[0]?.totalAll ?? journeyPages[0]?.total ?? 0,
    statusCounts: journeyPages[0]?.statusCounts ?? {},
  }
  const bugTotalCount = bugCountsQuery.data?.total ?? 0
  const bugOpenCount = bugCountsQuery.data?.openCount ?? 0
  const bugsProjectId = projectId
  const bugCountPending = bugCountsQuery.isLoading || bugCountsQuery.isFetching
  const overviewBugsPending = overviewBugsQuery.isLoading || overviewBugsQuery.isFetching
  const bugBreakdownPending = bugBreakdownQuery.isLoading || bugBreakdownQuery.isFetching

  const bugItems = overviewBugsQuery.data?.items ?? []
  const project = projectQuery.data ?? null

  // Recent test runs (test sessions) for the overview's Test Runs section.
  const testRunPages = testSessionsQuery.data?.pages ?? []
  const testRuns = testRunPages.flatMap(page => page.items)
  const testRunsLoading = testSessionsQuery.isLoading
  const testRunsProjectId = projectId

  const initialTestRun = useInitialTestRun(projectId, {
    testSessions: testRuns,
    testSessionsProjectId: projectId,
    testSessionsLoading: testSessionsQuery.isLoading,
    explorations: explorations.items,
    expTotal: explorations.total,
    explorationsProjectId: projectId,
    explorationsFetchPending: explorationsQuery.isLoading || explorationsQuery.isFetching,
    journeys: journeys.items,
    journeyTotal: journeys.totalAll,
    journeysProjectId: projectId,
    journeysFetchPending: journeysQuery.isLoading || journeysQuery.isFetching,
    journeyStatusCounts: journeys.statusCounts,
    bugTotalCount,
    bugsProjectId,
    bugCountPending,
    running: processingQuery.data?.running ?? [],
    project: project ?? undefined,
  })
  const { showInterim: showInitialTestRunInterim, initialSession } = initialTestRun

  const expCompleted = explorations.items.filter(e => e.status === 'completed').length
  const expTotal = explorations.total

  // First test session interim view — exits when the initial session completes (not when the first
  // exploration finishes).
  const showInterim = showInitialTestRunInterim

  // Dynamic Tab Alert: when the initial test run completes, notify the user via the browser tab
  // title/favicon if they've switched to another tab.
  const sawInterimRef = useRef(false)
  useEffect(() => {
    if (showInterim) sawInterimRef.current = true
  }, [showInterim])
  const initialTestRunJustCompleted =
    sawInterimRef.current && !showInterim && (initialSession?.status === 'completed' || expCompleted >= 1)
  const overviewProjectName = project?.id === projectId ? project.name : null
  useTabAlert(
    initialTestRunJustCompleted,
    overviewProjectName ? `✅ Analysis complete — ${overviewProjectName}` : '✅ QA analysis complete',
  )

  const journeyTotal = journeys.totalAll
  const totalBugs = bugTotalCount
  const statusDataLoading =
    explorations.currentProjectId !== projectId || explorations.loading ||
    journeys.currentProjectId !== projectId || journeys.loading
  const bugCountsLoading = bugsProjectId !== projectId || bugCountPending
  const bugTableLoading = bugsProjectId !== projectId || overviewBugsPending
  const bugChartLoading = bugsProjectId !== projectId || bugBreakdownPending
  const testRunDataLoading = testRunsProjectId !== projectId || testRunsLoading
  const overviewDecisionReady = !statusDataLoading && !bugCountsLoading && !testRunDataLoading

  if (showInterim) {
    return (
      <div className="overview-tab" data-testid="overview-tab">
        <InitialTestRunView
          projectId={projectId}
          targetUrl={initialTestRun.targetUrl}
          projectName={initialTestRun.projectName}
          explorationId={initialTestRun.explorationId}
          currentTask={initialTestRun.currentTask}
          journeyCount={initialTestRun.journeyCount}
          explorationCount={initialTestRun.explorationCount}
        />
      </div>
    )
  }

  // A GitHub-synced project with no activity yet and no app URL to explore never
  // kicks off an initial exploration — QA waits for deployment events instead. Show why, rather than
  // a wall of empty status cards.
  const githubProject = project?.id === projectId ? project.github : null
  const noActivityYet = overviewDecisionReady && expTotal === 0 && journeyTotal === 0 && totalBugs === 0
  if (githubProject && !project?.target_url && noActivityYet) {
    return (
      <div className="overview-tab" data-testid="overview-tab">
        <GithubQaStatus github={githubProject} />
      </div>
    )
  }

  return (
    <div className="overview-tab" data-testid="overview-tab">
      <OverviewStatCards
        projectId={projectId}
        openCount={bugOpenCount}
        testRunTotal={testSessionsQuery.data?.pages[0]?.total ?? testRuns.length}
        testRunLoading={testRunsLoading}
        bugCountLoading={bugCountsQuery.isLoading}
        hasUserSessions={userSessionsQuery.data?.has_sessions === true}
        securityEnabled={project?.enabled_polish_passes.includes('security') === true}
      />

      {(bugCountsLoading || totalBugs > 0) && (
        <section className="overview-tab__bugs-section">
          {bugCountsLoading ? (
            <div className="overview-tab__skeleton-text overview-tab__skeleton-text--bug-heading" role="status" aria-label="Loading bug count" />
          ) : (
            <UnresolvedBugsHeader count={bugOpenCount} />
          )}
          {bugCountsLoading || bugTableLoading ? (
            <div className="overview-tab__section-skeleton" role="status" aria-label="Loading bugs">
              {Array.from({ length: 4 }).map((_, index) => <span key={index} />)}
            </div>
          ) : (
            <TopOpenBugs bugs={bugItems} projectId={projectId} />
          )}
          {bugCountsLoading || bugChartLoading ? (
            <div className="overview-tab__chart-skeleton" role="status" aria-label="Loading bug breakdown" />
          ) : (
            <BugBreakdownChart
              breakdown={bugBreakdownQuery.data ?? []}
              resolvedCount={bugCountsQuery.data?.resolvedCount ?? 0}
              loading={bugBreakdownPending}
            />
          )}
        </section>
      )}

      {testRunDataLoading ? (
        <div className="overview-tab__section-skeleton" role="status" aria-label="Loading test runs">
          {Array.from({ length: 3 }).map((_, index) => <span key={index} />)}
        </div>
      ) : testRuns.length === 0 ? (
        <div className="overview-tab__runs-empty" role="status">
          No test runs yet
        </div>
      ) : (
        <OverviewTestRuns projectId={projectId} runs={testRuns} />
      )}

      <ApiAccessCallout />
    </div>
  )
})
